import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  X,
  User,
  Mail,
  Phone,
  Lock,
  Building,
  CreditCard,
  ShieldCheck,
  Save,
  AlertCircle,
  CheckCircle,
  Eye,
  EyeOff
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL;

function EditUserModal({ userId, onClose, onSuccess }) {
  // Form state
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    phone_number: '',
    role: 'user',
    bank_account: '',
    interbancario_bank_account: '',
    bank_name: '',
    name_on_bank_account: '',
    password: '', // New password (optional)
  });

  // UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  // Fetch user data on mount
  useEffect(() => {
    fetchUserData();
  }, [userId]);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/user/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const user = response.data;
      setFormData({
        username: user.username || '',
        email: user.email || '',
        phone_number: user.phone_number || '',
        role: user.role || 'user',
        bank_account: user.bank_account || '',
        interbancario_bank_account: user.interbancario_bank_account || '',
        bank_name: user.bank_name || '',
        name_on_bank_account: user.name_on_bank_account || '',
        password: '', // Always empty for security
      });
    } catch (error) {
      console.error('Error fetching user data:', error);
      setError('Error al cargar los datos del usuario');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {};

    // Username validation
    if (!formData.username.trim()) {
      errors.username = 'El nombre de usuario es requerido';
    } else if (formData.username.trim().length < 3) {
      errors.username = 'El nombre debe tener al menos 3 caracteres';
    }

    // Email validation
    if (!formData.email.trim()) {
      errors.email = 'El correo electrónico es requerido';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Correo electrónico inválido';
    }

    // Phone validation
    if (!formData.phone_number.trim()) {
      errors.phone_number = 'El teléfono es requerido';
    } else if (!/^\d{9}$/.test(formData.phone_number)) {
      errors.phone_number = 'El teléfono debe tener 9 dígitos';
    }

    // Password validation (only if provided)
    if (formData.password && formData.password.length < 6) {
      errors.password = 'La contraseña debe tener al menos 6 caracteres';
    }

    // Bank account validation (if provided)
    if (formData.bank_account && !/^\d{10,20}$/.test(formData.bank_account)) {
      errors.bank_account = 'Cuenta bancaria inválida (10-20 dígitos)';
    }

    // CCI validation (if provided)
    if (formData.interbancario_bank_account && !/^\d{20}$/.test(formData.interbancario_bank_account)) {
      errors.interbancario_bank_account = 'CCI debe tener 20 dígitos';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear field error when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
    setError('');
    setSuccessMessage('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setError('Por favor corrige los errores en el formulario');
      return;
    }

    setSaving(true);
    setError('');
    setSuccessMessage('');

    try {
      const token = localStorage.getItem('token');
      
      // Prepare data - only send fields that have values
      const updateData = {
        username: formData.username.trim(),
        email: formData.email.trim(),
        phone_number: formData.phone_number.trim(),
        role: formData.role,
      };

      // Add optional fields only if they have values
      if (formData.password) {
        updateData.password = formData.password;
      }
      if (formData.bank_account) {
        updateData.bank_account = formData.bank_account.trim();
      }
      if (formData.interbancario_bank_account) {
        updateData.interbancario_bank_account = formData.interbancario_bank_account.trim();
      }
      if (formData.bank_name) {
        updateData.bank_name = formData.bank_name.trim();
      }
      if (formData.name_on_bank_account) {
        updateData.name_on_bank_account = formData.name_on_bank_account.trim();
      }

      const response = await axios.put(
        `${API_URL}/api/user/${userId}`,
        updateData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          } 
        }
      );
      
      console.log('Update response:', response.data);

      setSuccessMessage('¡Usuario actualizado exitosamente!');
      
      // Wait a bit to show success message, then close
      setTimeout(() => {
        if (onSuccess) onSuccess();
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Error updating user:', error);
      
      if (error.response?.data?.error) {
        setError(error.response.data.error);
      } else if (error.response?.status === 409) {
        setError('El email o teléfono ya está en uso');
      } else {
        setError('Error al actualizar el usuario. Por favor intenta de nuevo.');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 border-4 border-[#B1336E] border-t-transparent rounded-full animate-spin"></div>
            <p className="text-sm text-slate-600 font-montserrat">Cargando datos...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
        <div
          className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex-shrink-0 px-6 py-5 border-b border-slate-200 bg-gradient-to-r from-slate-500/5 to-transparent">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-slate-500/10 rounded-lg">
                  <User className="w-6 h-6 text-slate-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-800 font-montserrat">
                    Editar Usuario
                  </h3>
                  <p className="text-sm text-slate-500 font-montserrat">
                    Actualizar información del usuario #{userId}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                disabled={saving}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-5">
            <div className="space-y-6">
              {/* Success Message */}
              {successMessage && (
                <div className="flex items-start gap-2 rounded-xl bg-green-50 border border-green-200 px-4 py-3">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-green-700 font-montserrat">{successMessage}</p>
                </div>
              )}

              {/* Error Message */}
              {error && (
                <div className="flex items-start gap-2 rounded-xl bg-red-50 border border-red-200 px-4 py-3">
                  <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-red-700 font-montserrat">{error}</p>
                </div>
              )}

              {/* Basic Information */}
              <div className="space-y-4">
                <h4 className="text-sm font-semibold text-slate-700 font-montserrat border-b border-slate-200 pb-2">
                  Información Básica
                </h4>

                {/* Username */}
                <div>
                  <label className="flex items-center gap-2 mb-2 text-sm font-semibold text-slate-800 font-montserrat">
                    <User className="h-4 w-4 text-[#B1336E]" />
                    Nombre de Usuario *
                  </label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => handleInputChange('username', e.target.value)}
                    className={`w-full rounded-xl border-2 ${
                      fieldErrors.username ? 'border-red-300' : 'border-slate-200'
                    } bg-white px-4 py-2.5 text-sm focus:outline-none focus:border-[#B1336E] transition-all font-montserrat`}
                    placeholder="Juan Pérez"
                  />
                  {fieldErrors.username && (
                    <p className="mt-1 text-xs text-red-600 font-montserrat">{fieldErrors.username}</p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label className="flex items-center gap-2 mb-2 text-sm font-semibold text-slate-800 font-montserrat">
                    <Mail className="h-4 w-4 text-[#E6B43C]" />
                    Correo Electrónico *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className={`w-full rounded-xl border-2 ${
                      fieldErrors.email ? 'border-red-300' : 'border-slate-200'
                    } bg-white px-4 py-2.5 text-sm focus:outline-none focus:border-[#E6B43C] transition-all font-montserrat`}
                    placeholder="usuario@ejemplo.com"
                  />
                  {fieldErrors.email && (
                    <p className="mt-1 text-xs text-red-600 font-montserrat">{fieldErrors.email}</p>
                  )}
                </div>

                {/* Phone */}
                <div>
                  <label className="flex items-center gap-2 mb-2 text-sm font-semibold text-slate-800 font-montserrat">
                    <Phone className="h-4 w-4 text-[#67A43C]" />
                    Teléfono *
                  </label>
                  <div className="flex items-center gap-2 w-full rounded-xl border-2 border-slate-200 bg-white px-4 py-2.5 focus-within:border-[#67A43C] transition-all">
                    <span className="text-sm text-slate-600 font-montserrat">🇵🇪 +51</span>
                    <input
                      type="tel"
                      value={formData.phone_number}
                      onChange={(e) => {
                        const digits = e.target.value.replace(/\D/g, '').slice(0, 9);
                        handleInputChange('phone_number', digits);
                      }}
                      placeholder="987654321"
                      maxLength={9}
                      className="flex-1 outline-none text-sm font-montserrat"
                    />
                  </div>
                  {fieldErrors.phone_number && (
                    <p className="mt-1 text-xs text-red-600 font-montserrat">{fieldErrors.phone_number}</p>
                  )}
                </div>

                {/* Role */}
                <div>
                  <label className="flex items-center gap-2 mb-2 text-sm font-semibold text-slate-800 font-montserrat">
                    <ShieldCheck className="h-4 w-4 text-[#B1336E]" />
                    Rol *
                  </label>
                  <select
                    value={formData.role}
                    onChange={(e) => handleInputChange('role', e.target.value)}
                    className="w-full rounded-xl border-2 border-slate-200 bg-white px-4 py-2.5 text-sm focus:outline-none focus:border-[#B1336E] transition-all font-montserrat"
                  >
                    <option value="user">Cliente</option>
                    <option value="driver">Conductor</option>
                    <option value="admin">Administrador</option>
                  </select>
                </div>

                {/* Password (Optional) */}
                <div>
                  <label className="flex items-center gap-2 mb-2 text-sm font-semibold text-slate-800 font-montserrat">
                    <Lock className="h-4 w-4 text-slate-600" />
                    Nueva Contraseña
                    <span className="text-xs font-normal text-slate-500">(opcional)</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      className={`w-full rounded-xl border-2 ${
                        fieldErrors.password ? 'border-red-300' : 'border-slate-200'
                      } bg-white px-4 py-2.5 pr-12 text-sm focus:outline-none focus:border-slate-400 transition-all font-montserrat`}
                      placeholder="Dejar en blanco para no cambiar"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-100 rounded"
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4 text-slate-400" />
                      ) : (
                        <Eye className="h-4 w-4 text-slate-400" />
                      )}
                    </button>
                  </div>
                  {fieldErrors.password && (
                    <p className="mt-1 text-xs text-red-600 font-montserrat">{fieldErrors.password}</p>
                  )}
                  <p className="mt-1 text-xs text-slate-500 font-montserrat">
                    Mínimo 6 caracteres. Dejar en blanco para mantener la contraseña actual.
                  </p>
                </div>
              </div>

              {/* Bank Information */}
              <div className="space-y-4">
                <h4 className="text-sm font-semibold text-slate-700 font-montserrat border-b border-slate-200 pb-2">
                  Información Bancaria (Opcional)
                </h4>

                {/* Bank Name */}
                <div>
                  <label className="flex items-center gap-2 mb-2 text-sm font-semibold text-slate-800 font-montserrat">
                    <Building className="h-4 w-4 text-[#E6B43C]" />
                    Banco
                  </label>
                  <input
                    type="text"
                    value={formData.bank_name}
                    onChange={(e) => handleInputChange('bank_name', e.target.value)}
                    className="w-full rounded-xl border-2 border-slate-200 bg-white px-4 py-2.5 text-sm focus:outline-none focus:border-[#E6B43C] transition-all font-montserrat"
                    placeholder="Ej: BCP, Interbank, BBVA"
                  />
                </div>

                {/* Account Holder Name */}
                <div>
                  <label className="flex items-center gap-2 mb-2 text-sm font-semibold text-slate-800 font-montserrat">
                    <User className="h-4 w-4 text-[#67A43C]" />
                    Titular de la Cuenta
                  </label>
                  <input
                    type="text"
                    value={formData.name_on_bank_account}
                    onChange={(e) => handleInputChange('name_on_bank_account', e.target.value)}
                    className="w-full rounded-xl border-2 border-slate-200 bg-white px-4 py-2.5 text-sm focus:outline-none focus:border-[#67A43C] transition-all font-montserrat"
                    placeholder="Nombre completo del titular"
                  />
                </div>

                {/* Bank Account */}
                <div>
                  <label className="flex items-center gap-2 mb-2 text-sm font-semibold text-slate-800 font-montserrat">
                    <CreditCard className="h-4 w-4 text-[#B1336E]" />
                    Número de Cuenta
                  </label>
                  <input
                    type="text"
                    value={formData.bank_account}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, '').slice(0, 20);
                      handleInputChange('bank_account', digits);
                    }}
                    className={`w-full rounded-xl border-2 ${
                      fieldErrors.bank_account ? 'border-red-300' : 'border-slate-200'
                    } bg-white px-4 py-2.5 text-sm focus:outline-none focus:border-[#B1336E] transition-all font-montserrat font-mono`}
                    placeholder="1234567890"
                  />
                  {fieldErrors.bank_account && (
                    <p className="mt-1 text-xs text-red-600 font-montserrat">{fieldErrors.bank_account}</p>
                  )}
                </div>

                {/* CCI */}
                <div>
                  <label className="flex items-center gap-2 mb-2 text-sm font-semibold text-slate-800 font-montserrat">
                    <CreditCard className="h-4 w-4 text-[#E6B43C]" />
                    CCI (Código Cuenta Interbancario)
                  </label>
                  <input
                    type="text"
                    value={formData.interbancario_bank_account}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, '').slice(0, 20);
                      handleInputChange('interbancario_bank_account', digits);
                    }}
                    className={`w-full rounded-xl border-2 ${
                      fieldErrors.interbancario_bank_account ? 'border-red-300' : 'border-slate-200'
                    } bg-white px-4 py-2.5 text-sm focus:outline-none focus:border-[#E6B43C] transition-all font-montserrat font-mono`}
                    placeholder="00212345678901234567"
                    maxLength={20}
                  />
                  {fieldErrors.interbancario_bank_account && (
                    <p className="mt-1 text-xs text-red-600 font-montserrat">{fieldErrors.interbancario_bank_account}</p>
                  )}
                  <p className="mt-1 text-xs text-slate-500 font-montserrat">
                    Debe tener exactamente 20 dígitos
                  </p>
                </div>
              </div>
            </div>
          </form>

          {/* Footer */}
          <div className="flex-shrink-0 px-6 py-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-5 py-2.5 rounded-xl border-2 border-slate-200 hover:bg-white transition-all text-sm font-medium text-slate-700 disabled:opacity-50 font-montserrat"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={saving}
              className="px-5 py-2.5 rounded-xl text-white transition-all text-sm font-medium disabled:opacity-50 shadow-lg font-montserrat flex items-center gap-2"
              style={{ backgroundColor: '#67A43C' }}
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Guardando...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Guardar Cambios
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default EditUserModal;